import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  setDoc,
  updateDoc,
  deleteDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';
import { db, hasConfig } from '../auth/firebaseClient';

const toMillis = (value) => {
  if (!value) return null;
  if (typeof value === 'number') return value;
  if (value?.toMillis) return value.toMillis();
  if (value?.seconds) return value.seconds * 1000;
  const parsed = Date.parse(String(value));
  return Number.isNaN(parsed) ? null : parsed;
};

const albumsRef = () => collection(db, 'albums');

let jsonAlbums = null;

const loadJsonAlbums = () => {
  if (jsonAlbums) return jsonAlbums;

  try {
    const albumsData = require('../data/albums.json');
    const albums = albumsData.albums || [];

    jsonAlbums = albums.map(album => ({
      id: album.id,
      slug: album.slug,
      name: album.name,
      subtitle: album.subtitle || '',
      description: album.description || '',
      avatar: album.avatarPath || '',
      logo: album.logoPath || '',
      hero: album.heroPath || '',
      createdAt: new Date(album.createdAt).getTime(),
      updatedAt: album.updatedAt ? new Date(album.updatedAt).getTime() : null,
      isPublished: album.isPublished || false,
      source: 'json',
    }));

    console.log('[AlbumService] Loaded', jsonAlbums.length, 'albums from JSON');
    return jsonAlbums;
  } catch (e) {
    console.error('[AlbumService] Error loading JSON albums:', e);
    return [];
  }
};

export const getAllAlbums = async (options = {}) => {
  const jsonData = loadJsonAlbums();
  console.log('[AlbumService] Returning', jsonData.length, 'albums from JSON');
  return jsonData;
};

export const getAlbumBySlug = async (slug) => {
  if (!slug) return null;
  
  const jsonData = loadJsonAlbums();
  const jsonAlbum = jsonData.find(a => a.slug === slug);
  
  let firestoreAlbum = null;
  if (hasConfig && db) {
    try {
      const q = query(albumsRef(), where('slug', '==', slug), limit(1));
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        firestoreAlbum = {
          id: doc.id,
          ...doc.data(),
          createdAt: toMillis(doc.data().createdAt),
        };
      }
    } catch (e) {
      console.error('Error fetching Firestore album:', e);
    }
  }
  
  if (!jsonAlbum && !firestoreAlbum) return null;
  
  return {
    ...(jsonAlbum || {}),
    ...(firestoreAlbum || {}),
    slug,
  };
};

export const createAlbumMetadata = async (albumData) => {
  if (!hasConfig || !db) throw new Error('Firestore not configured');
  
  const docRef = doc(albumsRef(), albumData.slug);
  
  await setDoc(docRef, {
    ...albumData,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    stats: albumData.stats || {
      categories: 0,
      photos: 0,
      videos: 0,
      views: 0,
      storageUsed: '0',
      bandwidth: '0',
    },
    access: albumData.access || [],
  }, { merge: true });
  
  return { id: albumData.slug, ...albumData };
};

export const updateAlbumMetadata = async (slug, updates) => {
  if (!hasConfig || !db || !slug) return;
  
  const updateData = {
    ...updates,
    updatedAt: serverTimestamp(),
  };
  
  await updateDoc(doc(db, 'albums', slug), updateData);
};

export const deleteAlbumMetadata = async (slug) => {
  if (!hasConfig || !db || !slug) return;
  await deleteDoc(doc(db, 'albums', slug));
};

export const addAlbumAccess = async (slug, userUid, role = 'viewer') => {
  if (!hasConfig || !db || !slug || !userUid) return;
  
  await updateDoc(doc(db, 'albums', slug), {
    access: arrayUnion({
      userUid,
      role,
      addedAt: Date.now(),
    }),
    updatedAt: serverTimestamp(),
  });
};

export const removeAlbumAccess = async (slug, userUid) => {
  if (!hasConfig || !db || !slug || !userUid) return;
  
  const albumDoc = await getDoc(doc(db, 'albums', slug));
  if (!albumDoc.exists()) return;
  
  const data = albumDoc.data();
  const access = data.access || [];
  const entry = access.find(a => a.userUid === userUid);
  
  if (entry) {
    await updateDoc(doc(db, 'albums', slug), {
      access: arrayRemove(entry),
      updatedAt: serverTimestamp(),
    });
  }
};

export const updateAlbumStats = async (slug, stats) => {
  if (!hasConfig || !db || !slug) return;
  
  await updateDoc(doc(db, 'albums', slug), {
    stats,
    updatedAt: serverTimestamp(),
  });
};

export const incrementAlbumViews = async (slug) => {
  if (!hasConfig || !db || !slug) return;
  
  const albumDoc = await getDoc(doc(db, 'albums', slug));
  if (!albumDoc.exists()) return;
  
  const data = albumDoc.data();
  const currentViews = data.stats?.views || 0;
  
  await updateDoc(doc(db, 'albums', slug), {
    'stats.views': currentViews + 1,
    updatedAt: serverTimestamp(),
  });
};

export const getAlbumStats = async () => {
  const albums = await getAllAlbums();
  
  let totalPhotos = 0;
  let totalVideos = 0;
  let totalViews = 0;
  let totalAlbums = albums.length;
  
  albums.forEach(album => {
    if (album.stats) {
      totalPhotos += album.stats.photos || 0;
      totalVideos += album.stats.videos || 0;
      totalViews += album.stats.views || 0;
    }
  });
  
  return {
    totalAlbums,
    totalPhotos,
    totalVideos,
    totalViews,
  };
};

export const JSON_ALBUMS = loadJsonAlbums();